"use client";

import { useEffect, useState } from "react";

const COLOR_SCHEME_QUERY = "(prefers-color-scheme: dark)";
const LOCAL_STORAGE_KEY = "theme-preference";

type Theme = "light" | "dark" | "system";

type ThemeOptions = {
  defaultTheme?: Theme;
  localStorageKey?: string;
};

type ThemeReturn = {
  theme: Theme;
  isDarkMode: boolean;
  setTheme: (theme: Theme) => void;
  cycle: () => void;
};

const getSystemPreference = (): boolean => {
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia(COLOR_SCHEME_QUERY).matches;
  }
  return false;
};

export function useTheme(options: ThemeOptions = {}): ThemeReturn {
  const { defaultTheme = "system", localStorageKey = LOCAL_STORAGE_KEY } =
    options;

  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === "undefined") {
      return defaultTheme;
    }

    try {
      const stored = window.localStorage.getItem(localStorageKey);
      if (stored && ["light", "dark", "system"].includes(stored)) {
        return stored as Theme;
      }
    } catch (error) {
      console.error(
        `Error reading localStorage key "${localStorageKey}":`,
        error,
      );
    }

    return defaultTheme;
  });

  const [systemPreference, setSystemPreference] = useState<boolean>(() =>
    getSystemPreference(),
  );

  const isDarkMode = theme === "system" ? systemPreference : theme === "dark";

  // Save theme to localStorage
  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      window.localStorage.setItem(localStorageKey, theme);
    } catch (error) {
      console.error(
        `Error setting localStorage key "${localStorageKey}":`,
        error,
      );
    }
  }, [theme, localStorageKey]);

  // Listen for system preference changes
  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia(COLOR_SCHEME_QUERY);

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemPreference(e.matches);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Apply dark class to document
  useEffect(() => {
    if (typeof window === "undefined") return;

    const root = window.document.documentElement;
    if (isDarkMode) {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [isDarkMode]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  const cycle = () => {
    setThemeState((current) => {
      const order: Theme[] = ["system", "light", "dark"];
      const currentIndex = order.indexOf(current);
      return order[(currentIndex + 1) % order.length];
    });
  };

  return {
    theme,
    isDarkMode,
    setTheme,
    cycle,
  };
}

export type { Theme, ThemeOptions, ThemeReturn };

// Legacy alias for backwards compatibility
export function useDarkMode() {
  const { theme, isDarkMode, cycle, setTheme } = useTheme();
  return {
    theme,
    isDarkMode,
    toggle: cycle,
    enable: () => setTheme("dark"),
    disable: () => setTheme("light"),
    set: (value: boolean) => setTheme(value ? "dark" : "light"),
  };
}
